import { useTranslation } from 'react-i18next';
import { useServerNow } from '@/hooks/useIssueNotices';
import { formatRemaining } from './issue-notice-meta';

interface Props {
  /** Null for an open-ended notice. */
  target: string | null;
  serverOffsetMs: number;
  /** 'ends' → "Ends in 4 min"; 'starts' → "Starts in 4 min". */
  kind?: 'ends' | 'starts';
  className?: string;
}

const FIVE_MIN = 5 * 60_000;

/**
 * UI-5: ticks every second under 5 minutes, otherwise every minute. The text
 * sits in aria-hidden + a static sr-only copy so screen readers are not told
 * every tick (UI-12).
 */
export function IssueCountdown({ target, serverOffsetMs, kind = 'ends', className }: Props) {
  const { t } = useTranslation();
  const targetMs = target ? Date.parse(target) : Number.MAX_SAFE_INTEGER;
  const coarseNow = useServerNow(serverOffsetMs, 60_000);
  const fine = targetMs - coarseNow < FIVE_MIN + 60_000;
  const now = useServerNow(serverOffsetMs, fine ? 1000 : 60_000);
  const remaining = Math.max(0, targetMs - now);
  if (!target) return <span className={className}>{t('issues.untilResolved')}</span>;
  const text = t(kind === 'ends' ? 'issues.endsIn' : 'issues.startsIn', { time: formatRemaining(remaining) });

  return (
    <span className={className}>
      <span aria-hidden>{text}</span>
      <span className="sr-only">{t(kind === 'ends' ? 'issues.endsAtSr' : 'issues.startsAtSr', { time: new Date(targetMs).toLocaleTimeString() })}</span>
    </span>
  );
}
